import { useState } from "react";
import { ReactComponent as InfoCircleSmall } from "../assets/Global/info-circle24px.svg";
import { ReactComponent as InfoCircleLarge } from "../assets/Global/info-circle48px.svg";
import { Button, IconButton, Typography, useTheme } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: {
    xs: "300px",
    sm: "350px",
    md: "500px",
  },
  bgcolor: "background.primary",
  borderRadius: "24px",
  border: "1px solid #fff",
  outline: "none",
  p: "24px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
};

interface InfoModalProps {
  title?: string;
  details?: string;
  buttonText?: string; // close button text
  color?: string;
  // controlled mode
  open?: boolean;
  onClose?: () => void;
  //hide the icon trigger
  hideTrigger?: boolean;
  //what happens when user clicks the modal button
  onButtonClick?: () => void;
}

function InfoModal({
  title,
  details,
  buttonText,
  color,
  open: openProp,
  onClose,
  hideTrigger = false,
  onButtonClick,
}: InfoModalProps) {
  const theme = useTheme();

  // if parent passes `open`, we use controlled mode, otherwise fallback to internal state
  const isControlled = typeof openProp === "boolean";
  const [openInternal, setOpenInternal] = useState(false);

  const open = isControlled ? openProp : openInternal;

  const handleOpen = () => {
    if (!isControlled) setOpenInternal(true);
  };

  const handleClose = () => {
    onClose?.();
    if (!isControlled) setOpenInternal(false);
  };

  const handleButton = () => {
    //do your action then close
    onButtonClick?.();
    handleClose();
  };

  return (
    <div>
      {!hideTrigger && (
        <IconButton onClick={handleOpen} aria-hidden={false}>
          <InfoCircleSmall style={{ color }} />
        </IconButton>
      )}

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={handleClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={open}>
          <Box sx={style}>
            <InfoCircleLarge
              style={{
                color: theme.palette.text.infoPrimary,
              }}
            />

            <Typography variant="h3" fontWeight={700}>
              {title}
            </Typography>

            <Typography
              variant="body2"
              component="p"
              fontWeight={300}
              dangerouslySetInnerHTML={{
                __html: details ?? "",
              }}
              sx={{
                whiteSpace: "balance",
                overflow: "auto",
                textAlign: "justify",
                "& ul": {
                  paddingInlineStart: 8,
                  mt: 3,
                },
                "& li": {
                  mb: 4,
                },
              }}
            />

            <Button
              sx={{
                alignSelf: "end",
                "&:hover": { backgroundColor: "transparent" },
                border: `1px solid ${theme.palette.border?.primary}`,
                color: theme.palette.text.infoPrimary,
                fontWeight: 300,
                fontSize: "14px",
                "@media (min-width:600px)": {
                  fontSize: "16px", // sm
                },
              }}
              onClick={handleButton}
            >
              {buttonText}
            </Button>
          </Box>
        </Fade>
      </Modal>
    </div>
  );
}
export default InfoModal;
